<template>
  <div
    class="queue-sidebar-root"
    :class="{ 'queue-sidebar-root--overlay': overlay }"
  >
    <!-- Backdrop — only rendered in overlay mode -->
    <div
      v-if="overlay && isOpen"
      class="queue-backdrop"
      @click="$emit('close')"
    ></div>

    <aside
      class="queue-sidebar"
      :class="{ 'queue-sidebar--open': isOpen }"
      :aria-hidden="!isOpen"
    >
      <button
        class="
          queue-edge-toggle-btn
          d-flex
          align-items-center
          justify-content-center
        "
        :title="isOpen ? 'Close activity panel' : 'Show activity panel'"
        @click="$emit('toggle')"
        aria-label="Toggle activity panel"
      >
        <i
          :class="
            isOpen
              ? 'fas fa-chevron-right queue-edge-icon'
              : 'fas fa-chevron-left queue-edge-icon'
          "
          aria-hidden="true"
        ></i>
        <span
          v-if="!isOpen && queue.length > 0"
          class="queue-edge-badge"
          aria-label="`${queue.length} messages queued`"
          >{{ queue.length > 9 ? "9+" : queue.length }}</span
        >
      </button>
      <div class="queue-sidebar-inner d-flex flex-column">
        <div class="queue-specialist-section queue-specialist-section--queue">
          <h3 class="queue-specialist-title">Message Queue</h3>
          <!-- Content -->
          <div class="queue-content">
            <!-- Empty State -->
            <div
              v-if="queue.length === 0"
              class="
                queue-empty
                d-flex
                flex-column
                align-items-center
                justify-content-center
                text-center
              "
            >
              <i class="fas fa-inbox"></i>
              <p>No messages queued</p>
            </div>

            <!-- Queue List -->
            <div v-else class="queue-list d-flex flex-column gap-2">
              <div
                v-for="(item, index) in queue"
                :key="index"
                class="queue-item d-flex align-items-center gap-3"
              >
                <div
                  class="
                    queue-item-icon
                    d-flex
                    align-items-center
                    justify-content-center
                  "
                >
                  <i class="fas fa-check queue-item-check"></i>
                </div>
                <div class="queue-item-body d-flex flex-column">
                  <span class="queue-item-text">{{
                    item.text || "(attachment)"
                  }}</span>
                  <span class="queue-item-label">
                    {{
                      index === 0 && paused
                        ? "Paused"
                        : index === 0
                        ? "Up next"
                        : "Queued"
                    }}
                  </span>
                </div>
                <button
                  class="
                    queue-item-remove
                    d-flex
                    align-items-center
                    justify-content-center
                  "
                  title="Remove from queue"
                  @click="$emit('remove', index)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div
            v-if="queue.length > 0"
            class="queue-footer d-flex flex-column gap-2"
          >
            <button
              class="
                queue-pause-btn
                d-flex
                align-items-center
                justify-content-center
                gap-2
              "
              @click="$emit('toggle-pause')"
            >
              <i :class="paused ? 'fas fa-play' : 'fas fa-pause'"></i>
              <span>{{ paused ? "Resume Queue" : "Pause Queue" }}</span>
            </button>
            <div
              class="
                queue-ready-line
                d-flex
                align-items-center
                justify-content-center
                gap-2
              "
            >
              <i class="fas fa-clock queue-ready-icon"></i>
              <span
                >{{ queue.length }} message{{
                  queue.length !== 1 ? "s" : ""
                }}
                waiting</span
              >
            </div>
          </div>
        </div>

        <!-- Live activity board: the most recent turn's steps, from the same
             per-message `activity` the chat's status line renders. -->
        <div
          class="queue-specialist-section queue-specialist-section--activity"
        >
          <h3 class="queue-specialist-title">Activity</h3>
          <div class="queue-activity">
            <div
              v-if="!hasActivity"
              class="
                queue-empty
                d-flex
                flex-column
                align-items-center
                text-center
              "
            >
              <i class="fas fa-wave-square"></i>
              <p>No activity yet</p>
            </div>
            <template v-else>
              <div class="queue-activity-status">
                <template v-if="working">
                  <status-spinner :size="13" />
                  <shimmer-text class="queue-activity-headline"
                    >Working</shimmer-text
                  >
                  <span v-if="elapsedLabel" class="queue-activity-elapsed">{{
                    elapsedLabel
                  }}</span>
                </template>
                <span v-else class="queue-activity-headline">{{
                  workedLabel
                }}</span>
              </div>
              <div
                v-if="activitySteps.length"
                ref="activityScroll"
                class="queue-activity-steps"
                @scroll="handleActivityScroll"
              >
                <activity-step-list
                  :steps="activitySteps"
                  :working="working"
                  :done="finished"
                  size="sm"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
import ActivityStepList from "@/components/OliverBotV2/chat/ActivityStepList.vue";
import ShimmerText from "@/components/OliverBotV2/shared/ShimmerText.vue";
import StatusSpinner from "@/components/OliverBotV2/shared/StatusSpinner.vue";
import {
  finishedWork,
  formatElapsed,
  isWorking,
  workedLine,
} from "@/components/OliverBotV2/utils/reasoningSummary";

const ACTIVITY_BOTTOM_THRESHOLD_PX = 48;

export default {
  name: "QueueSidebar",
  components: { ActivityStepList, ShimmerText, StatusSpinner },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    queue: {
      type: Array,
      default: () => [],
    },
    paused: {
      type: Boolean,
      default: false,
    },
    overlay: {
      type: Boolean,
      default: false,
    },
    // The most recent turn's activity trail: { steps, done, ... }. Null when no
    // turn has run yet in this chat.
    activity: {
      type: Object,
      default: null,
    },
  },
  emits: ["close", "remove", "toggle-pause"],
  data() {
    return { now: Date.now() };
  },
  computed: {
    activitySteps() {
      return this.activity?.steps || [];
    },
    // A turn still running counts before its first step, so the board never
    // shows the previous turn as done while the next one works.
    hasActivity() {
      return (
        this.activitySteps.length > 0 ||
        Boolean(this.activity && !this.activityDone)
      );
    },
    activityDone() {
      return Boolean(this.activity?.done);
    },
    working() {
      return isWorking(this.activity);
    },
    finished() {
      return finishedWork(this.activity);
    },
    workedLabel() {
      return workedLine(this.activity);
    },
    elapsedLabel() {
      return this.working && this.activity.startedAt
        ? formatElapsed(this.now - this.activity.startedAt)
        : "";
    },
  },
  watch: {
    "activity.steps.length": "scheduleActivityFollow",
    working: {
      immediate: true,
      handler(on) {
        clearInterval(this._clock);
        this._clock = null;
        if (!on) return;
        this.now = Date.now();
        this._clock = setInterval(() => (this.now = Date.now()), 1000);
      },
    },
  },
  beforeUnmount() {
    clearInterval(this._clock);
  },
  methods: {
    scheduleActivityFollow() {
      this.$nextTick(this.followActivityTail);
    },
    /**
     * Following stops when the READER moves up, not when the board grows.
     *
     * Distance from the bottom alone is not enough: a step arriving pushes the
     * content down, so the very next scroll event measures a long way from the
     * bottom and following switches itself off while nobody has touched
     * anything. Only scrollTop actually decreasing means they scrolled away.
     */
    handleActivityScroll(event) {
      const el = event.target;
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      const movedUp = el.scrollTop < this._lastActivityScrollTop - 1;
      this._lastActivityScrollTop = el.scrollTop;

      if (distanceFromBottom <= ACTIVITY_BOTTOM_THRESHOLD_PX) {
        this._followActivity = true;
      } else if (movedUp) {
        this._followActivity = false;
      }
    },
    followActivityTail() {
      if (this._followActivity === false) return;
      const el = this.$refs.activityScroll;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      this._lastActivityScrollTop = el.scrollTop;
    },
  },
};
</script>

<style lang="scss" scoped>
// ─── Root wrapper ────────────────────────────────────────────────────────────
.queue-sidebar-root {
  display: contents;

  &--overlay {
    display: block;
    position: absolute;
    inset: 0;
    z-index: 100;
    pointer-events: none;

    > * {
      pointer-events: all;
    }
  }
}

// ─── Backdrop (overlay mode only) ────────────────────────────────────────────
.queue-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);
  cursor: pointer;
}

// ─── Sidebar panel — push mode ───────────────────────────────────────────────
.queue-sidebar {
  flex-shrink: 0;
  width: 0;
  height: 100%;
  overflow: visible;
  background: var(--oliver-panel-bg, rgba(238, 233, 227, 0.98));
  border-left: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.12);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;

  &--open {
    // Comfortable, near-fixed width: at least 300px so the activity board is
    // never cramped, up to 360px on a big monitor.
    width: clamp(300px, 30%, 360px);
  }

  position: relative; /* allow absolute toggle button */

  // ─── Sidebar panel — overlay mode ──────────────────────────────────────────
  // Narrow Oliver: the drawer floats over the chat. It targets the same
  // comfortable ~320px as push mode (capped, so it stops being a near-full
  // sheet and leaves real chat + dim backdrop visible on mid-small windows),
  // and only shrinks toward 240px — leaving a strip to tap-close — when the
  // panel is genuinely tiny.
  .queue-sidebar-root--overlay & {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: clamp(240px, calc(100% - 56px), 360px);
    opacity: 1;
    overflow: visible;
    border-left: none;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &.queue-sidebar--open {
      transform: translateX(0);
      width: clamp(240px, calc(100% - 56px), 360px);
    }
  }
}

.queue-sidebar-inner {
  width: 100%;
  height: 100%;
  padding-right: 12px;
  border-left: 1px solid var(--oliver-accent-border);
  background: var(--oliver-gradient);
}

.queue-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 0;
  scrollbar-width: thin;
  scrollbar-color: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2)) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2));
    border-radius: 2px;
  }
}

.queue-empty {
  padding: 40px 0;
  color: var(--oliver-text-muted);

  i {
    font-size: 28px;
    margin-bottom: 12px;
    opacity: 0.45;
  }

  p {
    font-size: 14px;
    font-weight: 500;
    margin: 0;
  }
}

.queue-list {
  display: block;
}

.queue-item {
  padding: 10px 12px;
  border-radius: 12px;
  margin-bottom: 4px;
  background: var(--oliver-indicator-bg, rgba(0, 0, 0, 0.03));
  border: 1px solid var(--oliver-indicator-border, rgba(0, 0, 0, 0.06));
  transition: background 0.2s ease;
  animation: fadeIn 0.2s ease;

  &:hover {
    background: var(--oliver-btn-subtle-hover);
  }
}

.queue-item-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--oliver-accent);
  flex-shrink: 0;
}

.queue-item-check {
  font-size: 12px;
  color: #fff;
}

.queue-item-body {
  flex: 1;
  min-width: 0;
}

.queue-item-text {
  font-size: 13px;
  font-weight: 500;
  color: var(--oliver-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.queue-item-label {
  font-size: 11px;
  color: var(--oliver-text-muted);
}

.queue-item-remove {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--oliver-text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;

  i {
    font-size: 10px;
  }

  &:hover {
    background: rgba(229, 62, 62, 0.1);
    color: #e53e3e;
  }
}

.queue-footer {
  padding: 12px 0;
  flex-shrink: 0;
  border-top: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
}

.queue-specialist-section {
  padding: 12px 0;
  margin: 0 12px;
  overflow-y: auto;
}

// The queue sizes to its content (capped, then scrolls); the activity board
// takes the remaining height beneath it.
// Never compressed by the board below it. `flex-shrink: 0` is the point: with a
// shrink of 1 a long trail took height from the queue, which then scrolled
// internally — the queue got smaller because of activity that has nothing to do
// with it. It still caps at 45% and scrolls when the QUEUE itself is long.
.queue-specialist-section--queue {
  flex: 0 0 auto;
  max-height: 45%;
  border-bottom: 1px solid var(--oliver-accent-border);
}

// Grows with the trail and stops at whatever room is left — no cap of its own.
// `flex-grow: 0` keeps an empty board the height of "No activity yet" instead of
// stranding it in a tall box; `flex-shrink: 1` with `min-height: 0` is what lets
// a long trail take the rest of the sidebar and scroll inside itself.
.queue-specialist-section--activity {
  flex: 0 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.queue-specialist-title {
  margin: 0 0 8px 0;
  font-size: 15px;
  color: var(--oliver-text-muted);
  font-weight: 600;
}

// Card wrapper so the trail reads as a contained panel, not floating text.
.queue-activity {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--oliver-indicator-bg, rgba(0, 0, 0, 0.03));
  border: 1px solid var(--oliver-indicator-border, rgba(0, 0, 0, 0.06));
}

// The scrolling part — but only once the section above has hit its cap. Below
// that it is sized by the steps themselves, so three steps take three rows.
.queue-activity-steps {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2)) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2));
    border-radius: 2px;
  }
}

.queue-activity-elapsed {
  font-size: 12px;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
}

.queue-activity-status {
  flex: 0 0 auto;
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  font-size: 13px;
  font-weight: 600;
  color: var(--oliver-text-muted);

  // Nothing below it yet: no rule to separate it from.
  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
}

.queue-activity-headline {
  flex-grow: 1;
}

.queue-activity-status .status-spinner {
  align-self: center;
}

.queue-pause-btn {
  width: 100%;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 10px;
  background: var(--oliver-btn-subtle-bg, rgba(0, 0, 0, 0.04));
  border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  color: var(--oliver-btn-subtle-color);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--oliver-btn-subtle-hover);
    color: var(--oliver-accent);
    border-color: var(--oliver-accent-border);
  }

  i {
    font-size: 12px;
  }
}

.queue-ready-line {
  font-size: 11px;
  color: var(--oliver-text-muted);
  margin-top: 4px;
}

.queue-ready-icon {
  font-size: 11px;
  color: var(--oliver-accent);
  margin-right: 6px;
}

.queue-edge-toggle-btn {
  position: absolute;
  left: -32px;
  top: 16px;
  width: 32px;
  height: 32px;
  border-radius: 6px 0 0 6px;
  border: 1px solid var(--oliver-accent-border);
  border-right: none;
  background: var(--oliver-btn-subtle-bg);
  color: var(--oliver-btn-subtle-color);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  overflow: visible;

  &:hover {
    filter: brightness(0.98);
  }
}

.queue-edge-icon {
  font-size: 14px;
}

.queue-edge-badge {
  position: absolute;
  top: -6px;
  left: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: var(--oliver-accent);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
  animation: badgePop 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes badgePop {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
